/**
 * Pagina publica de pagamento E-Commerce
 * Permite que comerciantes redirecionem clientes para pagar sem login
 *
 * URLs suportadas:
 * - /checkout?entity=ENTITY_ID&service=SERVICE_ID&amount=VALOR&order_id=ORDER&return=URL
 * - /pay?id_ent=ENTITY_ID&id_temp=TEMPLATE_ID&total=VALOR&order_id=ORDER&return=URL
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'v1';

const EcommercePayment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = async () => {
      try {
        // Suportar ambos os formatos de URL
        const entityId = searchParams.get('entity') || searchParams.get('id_ent') || '';
        const serviceId = searchParams.get('service') || searchParams.get('id_temp') || '';
        const amount = searchParams.get('amount') || searchParams.get('total') || '';
        const orderId = searchParams.get('order_id') || searchParams.get('orderId') || '';
        const returnUrl = searchParams.get('return') || searchParams.get('return_url') || '';
        const notifyUrl = searchParams.get('notify') || searchParams.get('notify_url') || '';

        if (!entityId) {
          setError('Dados de integração em falta. Contacte o comerciante.');
          setLoading(false);
          return;
        }

        // Se temos id_temp, usar directamente a API de e-commerce
        // Se temos entity+service, buscar o template primeiro
        let templateId = serviceId;

        if (!templateId && entityId) {
          // Buscar template da entidade
          const resp = await fetch(`${API_URL}/${API_VERSION}/public/ecommerce/template?entity=${entityId}`);
          const data = await resp.json();
          if (data.success && data.data?.templateId) {
            templateId = data.data.templateId;
          }
        }

        // Redirecionar para a API que gera a pagina de pagamento
        const apiUrl = `${API_URL}/${API_VERSION}/public/ecommerce/payment`;

        // Criar form e submeter para a API
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = apiUrl;

        const fields: Record<string, string> = {
          id_ent: entityId,
          id_temp: templateId || entityId,
          total: amount || '0',
          order_id: orderId,
          return: returnUrl,
          notify: notifyUrl,
        };

        // Adicionar items do URL se existirem
        const itemNames = searchParams.getAll('item_name[]');
        const quantities = searchParams.getAll('quantity[]');
        const amounts = searchParams.getAll('amount[]');

        if (itemNames.length > 0) {
          itemNames.forEach(name => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'item_name[]';
            input.value = name;
            form.appendChild(input);
          });
          quantities.forEach(qty => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'quantity[]';
            input.value = qty;
            form.appendChild(input);
          });
          amounts.forEach(amt => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'amount[]';
            input.value = amt;
            form.appendChild(input);
          });
        }

        for (const [key, value] of Object.entries(fields)) {
          if (value) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          }
        }

        document.body.appendChild(form);
        form.submit();
      } catch (err: any) {
        setError(err.message || 'Erro ao processar pagamento');
        setLoading(false);
      }
    };

    init();
  }, [searchParams]);

  if (error) {
    return (
      <div style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: '#f8f7fc',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ background: '#4A1A6B', color: '#fff', padding: '12px 24px' }}>
          <strong style={{ fontSize: 18, letterSpacing: 2 }}>PAGALI</strong>
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            textAlign: 'center',
            background: '#fff',
            padding: 40,
            borderRadius: 12,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            maxWidth: 400,
            margin: 20,
          }}>
            <h2 style={{ color: '#dc3545', marginBottom: 12 }}>Erro</h2>
            <p style={{ color: '#6B5F80', fontSize: 14 }}>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: '#f8f7fc',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ background: '#4A1A6B', color: '#fff', padding: '12px 24px' }}>
        <strong style={{ fontSize: 18, letterSpacing: 2 }}>PAGALI</strong>
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 320, padding: '0 24px' }}>
          <div style={{
            border: '3px solid #e6e0f0',
            borderTop: '3px solid #4A1A6B',
            borderRadius: '50%',
            width: 40,
            height: 40,
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px',
          }} />
          <p style={{ color: '#2D2D3A', fontSize: 16, fontWeight: 600, marginBottom: 8 }}>A redirecionar para o pagamento</p>
          <p style={{ color: '#6B5F80', fontSize: 13, lineHeight: 1.5 }}>
            Está a ser redireccionado para a página de pagamento seguro. Por favor aguarde e não feche esta janela.
          </p>
          <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    </div>
  );
};

export default EcommercePayment;
